'use client';

import React from 'react';
import {
  ArrowRight,
  Check,
  ChevronDown,
  Clock,
  Image as ImageIcon,
  Mail,
  MapPin,
  Phone,
} from 'lucide-react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { safeHref } from '@/lib/storefront/safe-url';
import { resolveContactField, useStorefrontData } from '@/lib/storefront/storefront-data';
import { containerClass, sectionStyle } from './HeaderSections';

// ──────────────────────────────────────────────────────────────
// FAQ
// ──────────────────────────────────────────────────────────────

function FaqHeader({
  eyebrow,
  headline,
  subheadline,
}: {
  eyebrow?: string;
  headline?: string;
  subheadline?: string;
}) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      {eyebrow && (
        <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
      )}
      {headline && (
        <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
      )}
      {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
    </div>
  );
}

export function FaqAccordion({ content, style }: SectionProps) {
  const {
    eyebrow = 'FAQ',
    headline = 'Questions, answered',
    subheadline = 'The things customers ask before they book.',
    items = [],
    ctaText = '',
    ctaHref = '/contact',
  } = content;

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <FaqHeader eyebrow={eyebrow} headline={headline} subheadline={subheadline} />
        <div className="mx-auto mt-8 max-w-3xl divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {items.map((item: any, index: number) => (
            <details key={index} className="group px-5 py-4 sm:px-6">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-slate-900 marker:hidden">
                <span>{item.question}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-400 transition group-open:rotate-180" />
              </summary>
              {item.answer && (
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.answer}</p>
              )}
            </details>
          ))}
        </div>
        {ctaText && ctaHref && (
          <div className="mt-6 text-center">
            <a
              href={safeHref(ctaHref)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
            >
              {ctaText} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

export function FaqCards({ content, style }: SectionProps) {
  const {
    eyebrow = 'FAQ',
    headline = 'Questions, answered',
    subheadline = 'The things customers ask before they book.',
    items = [],
    ctaText = '',
    ctaHref = '/contact',
  } = content;

  return (
    <section className="w-full bg-slate-50" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <FaqHeader eyebrow={eyebrow} headline={headline} subheadline={subheadline} />
        <div className="mx-auto mt-8 grid max-w-4xl gap-4 sm:grid-cols-2">
          {items.map((item: any, index: number) => (
            <div key={index} className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
              {item.answer && (
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.answer}</p>
              )}
            </div>
          ))}
        </div>
        {ctaText && ctaHref && (
          <div className="mt-6 text-center">
            <a
              href={safeHref(ctaHref)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
            >
              {ctaText} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// Gallery
// ──────────────────────────────────────────────────────────────

const GALLERY_COLUMNS: Record<number, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
};

function GalleryHeader({
  eyebrow,
  headline,
  subheadline,
}: {
  eyebrow?: string;
  headline?: string;
  subheadline?: string;
}) {
  return (
    <div className="max-w-2xl">
      {eyebrow && (
        <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
      )}
      {headline && (
        <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
      )}
      {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
    </div>
  );
}

function GalleryFigure({ image, caption }: { image?: string; caption?: string }) {
  return (
    <figure className="group relative aspect-[4/3] overflow-hidden rounded-2xl bg-slate-100">
      {image ? (
        <img
          src={image}
          alt={caption || 'Gallery image'}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="flex h-full items-center justify-center text-slate-300">
          <ImageIcon className="h-10 w-10" />
        </div>
      )}
      {caption && (
        <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/80 to-transparent p-4 text-sm font-medium text-white">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

export function GalleryGrid({ content, style }: SectionProps) {
  const {
    eyebrow = 'Gallery',
    headline = 'A closer look',
    subheadline = 'The cars, the coast and the people behind the counter.',
    images = [],
    columns = 3,
  } = content;
  const cols = GALLERY_COLUMNS[Number(columns)] ?? GALLERY_COLUMNS[3];

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <GalleryHeader eyebrow={eyebrow} headline={headline} subheadline={subheadline} />
        <div className={`mt-8 grid gap-4 ${cols}`}>
          {images.map((item: any, index: number) => (
            <GalleryFigure key={index} image={item.image} caption={item.caption} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function GalleryMosaic({ content, style }: SectionProps) {
  const {
    eyebrow = 'Gallery',
    headline = 'A closer look',
    subheadline = 'The cars, the coast and the people behind the counter.',
    images = [],
  } = content;
  const visible = images.slice(0, 5);

  return (
    <section className="w-full bg-slate-50" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <GalleryHeader eyebrow={eyebrow} headline={headline} subheadline={subheadline} />
        <div className="mt-8 grid gap-3 sm:grid-cols-4 sm:grid-rows-2">
          {visible.map((item: any, index: number) => (
            <div
              key={index}
              className={index === 0 ? 'sm:col-span-2 sm:row-span-2' : ''}
            >
              <GalleryFigure image={item.image} caption={item.caption} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// Steps
// ──────────────────────────────────────────────────────────────

export function StepsNumbered({ content, style }: SectionProps) {
  const {
    eyebrow = 'How it works',
    headline = 'Three steps to the road',
    subheadline = 'No queues, no surprise fees, no paperwork mountain.',
    steps = [],
    ctaText = 'Start a booking',
    ctaHref = '/vehicles',
  } = content;

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-2xl text-center">
          {eyebrow && (
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
          )}
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step: any, index: number) => (
            <div key={index} className="relative">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">
                {index + 1}
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900">{step.title}</h3>
              {step.text && <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{step.text}</p>}
            </div>
          ))}
        </div>

        {ctaText && ctaHref && (
          <div className="mt-8 text-center">
            <a
              href={safeHref(ctaHref)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-700"
            >
              {ctaText} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

export function StepsCards({ content, style }: SectionProps) {
  const {
    eyebrow = 'How it works',
    headline = 'Three steps to the road',
    subheadline = 'No queues, no surprise fees, no paperwork mountain.',
    steps = [],
    ctaText = 'Start a booking',
    ctaHref = '/vehicles',
  } = content;

  return (
    <section className="w-full bg-slate-50" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-2xl text-center">
          {eyebrow && (
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
          )}
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {steps.map((step: any, index: number) => (
            <div key={index} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-xs font-bold text-brand">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="text-base font-semibold text-slate-900">{step.title}</h3>
              </div>
              {step.text && <p className="mt-3 text-sm leading-relaxed text-slate-600">{step.text}</p>}
            </div>
          ))}
        </div>

        {ctaText && ctaHref && (
          <div className="mt-8 text-center">
            <a
              href={safeHref(ctaHref)}
              className="inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white hover:bg-brand/90"
            >
              {ctaText} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// Stats
// ──────────────────────────────────────────────────────────────

export function StatsBand({ content, style }: SectionProps) {
  const {
    eyebrow = 'By the numbers',
    headline = 'A fleet you can count on',
    subheadline = '',
    stats = [],
    note = '',
  } = content;

  return (
    <section className="w-full bg-slate-900" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-2xl text-center">
          {eyebrow && (
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
          )}
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-300">{subheadline}</p>}
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat: any, index: number) => (
            <div key={index} className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center">
              <p className="text-3xl font-bold tracking-tight text-white">{stat.value}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-400">{stat.label}</p>
            </div>
          ))}
        </div>

        {note && <p className="mt-6 text-center text-xs text-slate-500">{note}</p>}
      </div>
    </section>
  );
}

export function StatsInline({ content, style }: SectionProps) {
  const {
    eyebrow = 'By the numbers',
    headline = '',
    subheadline = '',
    stats = [],
    note = '',
  } = content;

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        {(eyebrow || headline || subheadline) && (
          <div className="mx-auto max-w-2xl text-center">
            {eyebrow && (
              <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
            )}
            {headline && (
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
            )}
            {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
          </div>
        )}

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat: any, index: number) => (
            <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center">
              <p className="text-3xl font-bold tracking-tight text-slate-900">{stat.value}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {note && <p className="mt-6 text-center text-xs text-slate-400">{note}</p>}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// Location / map
// ──────────────────────────────────────────────────────────────

function LocationDetails({
  address,
  phone,
  email,
  hours,
  directionsUrl,
  dark = false,
}: {
  address?: string;
  phone?: string;
  email?: string;
  hours?: string;
  directionsUrl?: string;
  dark?: boolean;
}) {
  const row = `flex items-start gap-3 rounded-xl p-3 ${dark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`;
  const icon = `flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${dark ? 'bg-white/10 text-brand' : 'bg-brand/10 text-brand'}`;
  const label = dark ? 'text-slate-500' : 'text-slate-400';
  const value = dark ? 'text-white' : 'text-slate-800';

  return (
    <div className="space-y-1">
      {address && (
        <div className={row}>
          <span className={icon}><MapPin className="h-4 w-4" /></span>
          <span>
            <span className={`block text-[10px] font-semibold uppercase tracking-wider ${label}`}>Address</span>
            <span className={`block whitespace-pre-line text-sm font-medium ${value}`}>{address}</span>
          </span>
        </div>
      )}
      {phone && (
        <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className={row}>
          <span className={icon}><Phone className="h-4 w-4" /></span>
          <span>
            <span className={`block text-[10px] font-semibold uppercase tracking-wider ${label}`}>Phone</span>
            <span className={`block text-sm font-medium ${value}`}>{phone}</span>
          </span>
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} className={row}>
          <span className={icon}><Mail className="h-4 w-4" /></span>
          <span>
            <span className={`block text-[10px] font-semibold uppercase tracking-wider ${label}`}>Email</span>
            <span className={`block break-all text-sm font-medium ${value}`}>{email}</span>
          </span>
        </a>
      )}
      {hours && (
        <div className={row}>
          <span className={icon}><Clock className="h-4 w-4" /></span>
          <span>
            <span className={`block text-[10px] font-semibold uppercase tracking-wider ${label}`}>Opening hours</span>
            <span className={`block whitespace-pre-line text-sm font-medium ${value}`}>{hours}</span>
          </span>
        </div>
      )}
      {directionsUrl && (
        <a
          href={safeHref(directionsUrl)}
          target="_blank"
          rel="noreferrer"
          className={`ml-3 mt-2 inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold ${dark ? 'bg-white text-slate-900 hover:bg-slate-100' : 'bg-slate-900 text-white hover:bg-slate-700'}`}
        >
          Get directions <ArrowRight className="h-4 w-4" />
        </a>
      )}
    </div>
  );
}

export function LocationMap({ content, style }: SectionProps) {
  const { agency } = useStorefrontData();
  const {
    eyebrow = 'Find us',
    headline = 'Pick up in the heart of the city',
    subheadline = 'Free airport meet-and-greet on request.',
    mapEmbedUrl = '',
    directionsUrl = '',
    showMap = true,
  } = content;
  const address = resolveContactField(content.address, agency?.address);
  const phone = resolveContactField(content.phone, agency?.phone);
  const email = resolveContactField(content.email, agency?.email);
  const hours = resolveContactField(content.hours, agency?.hours);

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="grid gap-6 lg:grid-cols-2 lg:items-stretch">
          <div className="rounded-3xl bg-slate-900 p-7 text-white sm:p-9">
            {eyebrow && (
              <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
            )}
            <h2 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">{headline}</h2>
            {subheadline && <p className="mt-3 text-sm leading-relaxed text-slate-300">{subheadline}</p>}
            <div className="mt-6">
              <LocationDetails
                address={address}
                phone={phone}
                email={email}
                hours={hours}
                directionsUrl={directionsUrl}
                dark
              />
            </div>
          </div>

          <div className="min-h-[320px] overflow-hidden rounded-3xl border border-slate-200 bg-slate-100">
            {showMap && mapEmbedUrl ? (
              <iframe
                title="Location map"
                src={mapEmbedUrl}
                className="h-full min-h-[320px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="flex h-full min-h-[320px] flex-col items-center justify-center p-8 text-center">
                <MapPin className="h-10 w-10 text-slate-300" />
                <p className="mt-3 text-sm font-semibold text-slate-700">Map preview</p>
                <p className="mt-1 max-w-xs text-xs text-slate-500">
                  Add an OpenStreetMap embed URL in the section settings.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function LocationDetailsSection({ content, style }: SectionProps) {
  const { agency } = useStorefrontData();
  const {
    eyebrow = 'Find us',
    headline = 'Pick up in the heart of the city',
    subheadline = 'Free airport meet-and-greet on request.',
    directionsUrl = '',
  } = content;
  const address = resolveContactField(content.address, agency?.address);
  const phone = resolveContactField(content.phone, agency?.phone);
  const email = resolveContactField(content.email, agency?.email);
  const hours = resolveContactField(content.hours, agency?.hours);

  return (
    <section className="w-full bg-slate-50" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-7 shadow-sm sm:p-10">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
              <MapPin className="h-6 w-6" />
            </span>
            <div>
              {eyebrow && (
                <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
              )}
              <h2 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">{headline}</h2>
              {subheadline && <p className="mt-2 text-sm text-slate-600">{subheadline}</p>}
            </div>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <LocationDetails
              address={address}
              phone={phone}
              email={email}
              hours={hours}
              directionsUrl={directionsUrl}
            />
          </div>
          <div className="mt-6 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-800">
            <Check className="h-4 w-4" /> Free cancellation up to 24 hours before pick-up.
          </div>
        </div>
      </div>
    </section>
  );
}

export const FAQ_VARIATIONS = {
  accordion: FaqAccordion,
  cards: FaqCards,
} as const;

export const GALLERY_VARIATIONS = {
  grid: GalleryGrid,
  mosaic: GalleryMosaic,
} as const;

export const STEPS_VARIATIONS = {
  numbered: StepsNumbered,
  cards: StepsCards,
} as const;

export const STATS_VARIATIONS = {
  band: StatsBand,
  inline: StatsInline,
} as const;

export const LOCATION_VARIATIONS = {
  map: LocationMap,
  details: LocationDetailsSection,
} as const;
